import { useGetProductLinesQuery } from '@/services/productLines';
import { useMemo } from 'react';

import { EquipmentRecordStatus, ProgramType } from 'types';

import { CapSchedule, formatCapSchedule } from './capSchedules';

export const useProductLineNames = (skip = false) => {
  const { data, isLoading } = useGetProductLinesQuery(
    { status: EquipmentRecordStatus.active, programType: ProgramType.energyBackup },
    { skip },
  );
  const productLines = useMemo(() => data?.data ?? [], [data]);
  const namesById = useMemo(() => new Map(productLines.map((line) => [line.id, line.name])), [productLines]);
  return { productLines, namesById, isLoading };
};

/** Resolves inactive lines too, since a schedule keeps pointing at a line deactivated after it was saved. */
const useScheduleLineNames = (skip: boolean) => {
  const { namesById: activeNames } = useProductLineNames(skip);
  const { data: inactive } = useGetProductLinesQuery(
    { status: EquipmentRecordStatus.inactive, programType: ProgramType.energyBackup },
    { skip },
  );
  return useMemo(() => {
    const names = new Map((inactive?.data ?? []).map((line) => [line.id, `${line.name} (inactive)`]));
    activeNames.forEach((name, id) => names.set(id, name));
    return names;
  }, [activeNames, inactive]);
};

const fontSizeClass = { xs: 'text-xs', sm: 'text-sm' } as const;

interface CapSchedulesSummaryProps {
  schedules?: CapSchedule[];
  fontSize?: keyof typeof fontSizeClass;
}

export const CapSchedulesSummary = ({ schedules, fontSize = 'sm' }: CapSchedulesSummaryProps) => {
  const hasSchedules = !!schedules?.length;
  const namesById = useScheduleLineNames(!hasSchedules);

  if (!hasSchedules) return null;

  return (
    <div className={`flex flex-col gap-4 ${fontSizeClass[fontSize]}`}>
      {schedules.map((schedule) => (
        <div key={schedule.productLineId}>{formatCapSchedule(schedule, namesById.get(schedule.productLineId))}</div>
      ))}
    </div>
  );
};
